import React, { FC, ReactElement } from 'react';
import Box from '@mui/material/Box';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import { SxProps, styled } from '@mui/material';
import { ClearIcon, TimePicker } from '@mui/x-date-pickers-pro';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

interface TwTimePickerProps {
  /** Id for the text field box. */
  id?: string;
  /** Name for the text field box. */
  name?: string;
  /** Text that goes over the text field box. */
  label?: string;
  /** Placeholder of the text field box. */
  placeholder?: string;
  /** Classname for the text field. */
  textFieldClassName?: string;
  /** Classname for the container. */
  containerClassName?: string;
  /** Sx element for the container. */
  containerSx?: SxProps;
  /** Value of the datepicker. */
  value: Date | undefined | null;
  /** 12-hour time format. */
  ampm?: boolean;
  error?: boolean;
  /** Timepicker change event. */
  disabled?: boolean;
  onChange: (date: unknown) => void;
  /** Timepicker input change event. */
  onInputChange?: (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
  /** Timepicker input blur event. */
  onInputBlur?: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>) => void;
  /** Function to render a second field adjacent the time picker. */
  secondField?: () => ReactElement;
}

const StyledTimePickerLabel = styled('label')({
  color: '#7e858e',
  display: 'inline-block',
  fontSize: '14px',
  padding: '4px 0px',
  fontWeight: 500
});

const TextFieldSx = {
  backgroundColor: 'white',
  fontFamily: 'Poppins',
  fontSize: '14px',
  lineHeight: '21px',
  width: '100%',
  height: '40px',
  margin: '0 0 8px 0',
  padding: '0',
  minWidth: '130px',

  '& input': {
    fontFamily: 'Poppins',
    fontSize: '14px',
    padding: '10px 0px 10px 14px',

    '&::placeholder': {
      textTransform: 'uppercase'
    }
  },

  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#3959df',
    borderWidth: '2px'
  },

  '& .Mui-disabled fieldset': {
    borderColor: '#E2E2EA !important'
  },

  '& fieldset.MuiOutlinedInput-notchedOutline': {
    borderColor: '#E2E2EA !important'
  },

  '& .MuiChip-deleteIcon': {
    color: '#9CA3AF',
    width: '16px',
    height: '16px'
  }
};

const ClearTimePickerIcon = styled(ClearIcon)({
  width: '18px',
  height: '18px'
});

const OpenPickerIcon = styled(AccessTimeIcon)({
  width: '18px',
  height: '18px'
});

const TwTimePicker: FC<TwTimePickerProps> = ({
  id,
  name,
  label,
  placeholder,
  textFieldClassName,
  containerClassName,
  containerSx,
  value,
  error,
  onChange,
  onInputChange,
  onInputBlur,
  ampm,
  disabled,
  secondField
}) => {
  return (
    <Box className={containerClassName} sx={containerSx}>
      {!!label && (
        <StyledTimePickerLabel>
          <span>{label}</span>
        </StyledTimePickerLabel>
      )}
      <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: '10px', width: '100%' }}>
        <TimePicker
          value={value}
          name={name}
          ampm={ampm}
          format={ampm ? 'hh:mm aa' : 'HH:mm'}
          onChange={(newDate: Date | undefined | null): void => onChange(newDate)}
          disabled={disabled}
          slots={{
            // Slots only accept components, not functions, so we applied styles to the TextField in the slotProps below
            // This solves a bug where picker popper is misplaced when time is selected:
            // Issue example: https://github.com/mui/mui-x/issues/8411
            textField: TextField,
            ...(!secondField && { clearIcon: ClearTimePickerIcon }),
            openPickerIcon: OpenPickerIcon
          }}
          slotProps={{
            // Apparent bug in MUI where tab navigation is not working properly in this component
            // when we use onBlur event. Preventing focus on the elements in the Picker component and Paper fixes the issue for now in Chrome.
            // Safari still has issues with tab navigation.
            openPickerButton: { size: 'small', tabIndex: -1 },
            clearIcon: { tabIndex: -1 },
            clearButton: { size: 'small', tabIndex: -1 },
            desktopPaper: { tabIndex: -1 },
            openPickerIcon: { tabIndex: -1 },
            textField: {
              placeholder: placeholder,
              error: error,
              onChange: (e): void => {
                onInputChange && onInputChange(e);
              },
              onBlur: (e): void => {
                onInputBlur && onInputBlur(e);
              },
              id: id,
              name: name,
              className: textFieldClassName,
              inputProps: {
                tabIndex: 0,
                inputMode: 'text',
                style: { textTransform: 'uppercase' }
              },
              sx: TextFieldSx
            },
            field: { clearable: !secondField },
            actionBar: {
              actions: ['clear', 'accept']
            }
          }}
        />
        {secondField && secondField()}
      </Box>
    </Box>
  );
};

export default TwTimePicker;
